import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { API } from "../lib/config";
import { BrandMark, BrandWordmark } from "../components/BrandLogo";
import { SiteFooter } from "../components/SiteFooter";
import { highlevelPaymentsService } from "../services/highlevelPaymentsService";
import { ArrowLeft, ArrowRight, ShieldCheck, Lock, PhoneCall, ClipboardList, Rocket, BadgeCheck } from "lucide-react";

const PRIMARY_CTA = "bg-[var(--brand)] text-[var(--paper)] hover:bg-[var(--brand-strong)] focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2";

export default function ReservePage() {
  const navigate = useNavigate();
  const paymentLink = highlevelPaymentsService.getDepositPaymentLink();

  const handleStripeClick = () => {
    axios.post(`${API}/deposit-intent`, { source: "reserve-page-stripe-click" }).catch(() => {});
    window.open(paymentLink, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-background noise-overlay">
      <header className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-1.5 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2" aria-label="EnquiryOps home" data-testid="reserve-nav-logo">
            <BrandMark size={32} />
            <BrandWordmark className="text-lg" />
          </Link>
          <Button variant="ghost" className="rounded-xl" data-testid="reserve-back-button" onClick={() => navigate("/")}>
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to site
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-center mb-10 animate-fade-up">
          <Badge className="bg-[var(--navy-900)] text-[var(--paper)] hover:bg-[var(--navy-900)] mb-4">Founding cohort, 5 centres only</Badge>
          <h1 className="text-3xl sm:text-4xl font-semibold text-foreground">Reserve your founding-centre slot</h1>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto leading-relaxed">
            Your $199 deposit reserves a founding-centre slot. If we are not a fit after the call, you get it back. If you proceed, it is credited toward setup or your first month.
          </p>
        </div>

        {/* Deposit summary */}
        <Card className="rounded-2xl border-2 border-[var(--brand)] shadow-[var(--shadow-md)] mb-6">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <p className="font-heading font-semibold text-lg text-foreground">Founding Centre Deposit</p>
                <p className="text-sm text-muted-foreground">One of 5 slots · personally onboarded</p>
              </div>
              <div className="text-right">
                <p className="text-4xl font-heading font-semibold tabular-nums text-foreground">$199</p>
                <p className="text-xs text-[var(--brand-strong)] font-medium">100% refundable</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              {[
                { icon: ShieldCheck, label: "Refunded in full", sub: "if either side says it's not a fit" },
                { icon: BadgeCheck, label: "Credited forward", sub: "toward setup or first month if you proceed" },
                { icon: Lock, label: "Secure checkout", sub: "processed by Stripe" },
              ].map((i) => (
                <div key={i.label} className="bg-secondary rounded-xl p-3.5 text-center">
                  <i.icon className="h-4.5 w-4.5 mx-auto text-accent mb-1.5" />
                  <p className="text-xs font-semibold text-foreground">{i.label}</p>
                  <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{i.sub}</p>
                </div>
              ))}
            </div>
            <Button size="lg" className={`w-full rounded-xl h-13 text-base shadow-[var(--shadow-md)] ${PRIMARY_CTA}`} data-testid="reserve-stripe-payment-link" onClick={handleStripeClick}>
              <Lock className="h-4 w-4 mr-2" /> Place $199 Refundable Deposit via Stripe
            </Button>
            <p className="text-[11px] text-muted-foreground text-center mt-3">
              Opens Stripe's secure payment page in a new tab. No card details ever touch our servers.
            </p>
          </CardContent>
        </Card>

        {/* What happens next */}
        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)] mb-6">
          <CardContent className="p-6">
            <p className="font-heading font-semibold text-foreground mb-5">What happens after you pay</p>
            <div className="space-y-5">
              {[
                { icon: ClipboardList, step: "1", title: "Tell us about your centre (3 minutes)", body: "A short onboarding form: programmes, branches, enquiry volume, and where you feel families slip away." },
                { icon: PhoneCall, step: "2", title: "Book your fit call", body: "A 30-minute working session. We map your current enquiry flow and show exactly how EnquiryOps would run it." },
                { icon: ShieldCheck, step: "3", title: "Mutual fit decision", body: "If either side decides it's not right, your $199 comes straight back. No forms, no friction." },
                { icon: Rocket, step: "4", title: "Founding onboarding begins", body: "We set up your pipeline, WhatsApp templates, trial workflows, and train your team. Deposit credited to setup or month one." },
              ].map((s) => (
                <div key={s.step} className="flex gap-4">
                  <span className="h-9 w-9 rounded-full bg-[var(--brand-weak)] text-[var(--navy-900)] font-semibold text-sm flex items-center justify-center shrink-0">{s.step}</span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{s.title}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Refund policy */}
        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)] mb-8 bg-secondary">
          <CardContent className="p-6">
            <p className="font-semibold text-sm text-foreground mb-2">Refund policy, in plain language</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The deposit exists to filter for serious centre owners, not to trap anyone. You can request a refund any time before onboarding starts, for any reason, and we'll return it within 5 business days. If we decide your centre isn't a fit for the founding cohort, we refund it proactively.
            </p>
          </CardContent>
        </Card>

        <Separator className="mb-8" />

        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-3">Already placed your deposit?</p>
          <Button variant="secondary" className="rounded-xl border border-border" data-testid="reserve-continue-onboarding-button" onClick={() => navigate("/thank-you")}>
            Continue to onboarding <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
